import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, CalendarDays, Sparkles } from 'lucide-react';
import { useTransactions, useAccounts, useCategories } from '../hooks';
import { useDisplayProfile } from '../hooks/useDisplayProfile';
import { calculateBasicStats, calculateCategoryStats, calculateRealTotalBalance } from '../lib/services/StatisticsService';
import TransactionForm from '../components/TransactionForm';
import DonutChart from '../components/DonutChart';
import MetricCard from '../components/ui/MetricCard';
import RingProgress from '../components/ui/RingProgress';
import Sparkline from '../components/ui/Sparkline';
import ForecastWidget from '../components/dashboard/ForecastWidget';

interface DashboardStats {
  totalBalance: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  savingsRate: number;
  incomeChange: number;
  expensesChange: number;
  balanceChange: number;
  savingsRateChange: number;
}

interface CategoryData {
  name: string;
  amount: number;
  percentage: number;
  color: string;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);

const buildSeries = (transactions: any[], type: 'depense' | 'revenu' | 'net', days = 14) => {
  const today = new Date();
  const buckets = Array.from({ length: days }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (days - 1 - i));
    return d.toISOString().slice(0, 10);
  });
  return buckets.map((day) => {
    const dayTx = transactions.filter((t) => String(t.date).slice(0, 10) === day);
    if (type === 'depense') {
      return dayTx
        .filter((t) => t.type === 'depense')
        .reduce((s, t) => s + Math.abs(Number(t.montant) || 0), 0);
    }
    if (type === 'revenu') {
      return dayTx
        .filter((t) => t.type === 'revenu')
        .reduce((s, t) => s + Math.abs(Number(t.montant) || 0), 0);
    }
    return dayTx.reduce((s, t) => {
      const m = Math.abs(Number(t.montant) || 0);
      return s + (t.type === 'revenu' ? m : t.type === 'depense' ? -m : 0);
    }, 0);
  });
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { transactions, loading: transactionsLoading, addTransaction } = useTransactions();
  const { comptes, loading: accountsLoading } = useAccounts();
  const { categories, loading: categoriesLoading } = useCategories();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalBalance: 0,
    monthlyIncome: 0,
    monthlyExpenses: 0,
    savingsRate: 0,
    incomeChange: 0,
    expensesChange: 0,
    balanceChange: 0,
    savingsRateChange: 0,
  });
  const [categoryStats, setCategoryStats] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const currentDate = new Date();
        const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
          .toISOString()
          .split('T')[0];
        const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
          .toISOString()
          .split('T')[0];
        const previousMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
          .toISOString()
          .split('T')[0];
        const previousMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
          .toISOString()
          .split('T')[0];

        const [currentMonthStats, previousMonthStats, categoryStatsData, realBalance] =
          await Promise.all([
            calculateBasicStats(startDate, endDate),
            calculateBasicStats(previousMonthStart, previousMonthEnd),
            calculateCategoryStats(startDate, endDate),
            calculateRealTotalBalance(),
          ]);

        if (currentMonthStats.data && previousMonthStats.data) {
          const totalBalance = realBalance.data !== null ? realBalance.data : 0;
          const savingsRate =
            currentMonthStats.data.totalRevenus > 0
              ? ((currentMonthStats.data.totalRevenus - currentMonthStats.data.totalDepenses) /
                  currentMonthStats.data.totalRevenus) *
                100
              : 0;
          const previousSavingsRate =
            previousMonthStats.data.totalRevenus > 0
              ? ((previousMonthStats.data.totalRevenus - previousMonthStats.data.totalDepenses) /
                  previousMonthStats.data.totalRevenus) *
                100
              : 0;

          const incomeChange =
            previousMonthStats.data.totalRevenus > 0
              ? ((currentMonthStats.data.totalRevenus - previousMonthStats.data.totalRevenus) /
                  previousMonthStats.data.totalRevenus) *
                100
              : 0;
          const expensesChange =
            previousMonthStats.data.totalDepenses > 0
              ? ((currentMonthStats.data.totalDepenses - previousMonthStats.data.totalDepenses) /
                  previousMonthStats.data.totalDepenses) *
                100
              : 0;
          const balanceChange =
            previousMonthStats.data.solde !== 0
              ? ((currentMonthStats.data.solde - previousMonthStats.data.solde) /
                  Math.abs(previousMonthStats.data.solde)) *
                100
              : 0;

          setStats({
            totalBalance,
            monthlyIncome: currentMonthStats.data.totalRevenus,
            monthlyExpenses: currentMonthStats.data.totalDepenses,
            savingsRate: Math.round(savingsRate * 10) / 10,
            incomeChange: Math.round(incomeChange * 10) / 10,
            expensesChange: Math.round(expensesChange * 10) / 10,
            balanceChange: Math.round(balanceChange * 10) / 10,
            savingsRateChange: Math.round((savingsRate - previousSavingsRate) * 10) / 10,
          });
        }

        if (categoryStatsData.data && categoryStatsData.data.length > 0) {
          setCategoryStats(
            categoryStatsData.data
              .filter((cat) => cat.total > 0)
              .slice(0, 5)
              .map((cat) => ({
                name: cat.categorie_nom,
                amount: cat.total,
                percentage: Math.round(cat.pourcentage * 10) / 10,
                color: cat.categorie_couleur || '#666',
              })),
          );
        } else {
          setCategoryStats([]);
        }
      } catch (error) {
        console.error('Erreur lors du calcul des statistiques:', error);
      } finally {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    };

    if (!transactionsLoading && !accountsLoading && !categoriesLoading) {
      loadStats();
    }
  }, [transactions, comptes, categories, transactionsLoading, accountsLoading, categoriesLoading]);

  const balanceSeries = useMemo(() => buildSeries(transactions, 'net'), [transactions]);
  const incomeSeries = useMemo(() => buildSeries(transactions, 'revenu'), [transactions]);
  const expenseSeries = useMemo(() => buildSeries(transactions, 'depense'), [transactions]);

  const recentTransactions = useMemo(() => {
    return [...transactions]
      .sort((a, b) => String(b.date).localeCompare(String(a.date)))
      .slice(0, 5);
  }, [transactions]);

  const { firstName: greetingName } = useDisplayProfile();

  const periodLabel = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d: Date) =>
      d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${fmt(start)} — ${fmt(end)}`;
  }, []);

  const monthlySavings = stats.monthlyIncome - stats.monthlyExpenses;
  const topBudgetCategory = categoryStats[0];
  const budgetUsedPct = topBudgetCategory
    ? Math.min(100, Math.round(topBudgetCategory.percentage * 1.4))
    : 0;

  const insightText =
    stats.expensesChange < 0
      ? `Vos dépenses ont diminué de ${Math.abs(stats.expensesChange).toFixed(1).replace('.', ',')} % ce mois-ci. Excellent travail !`
      : stats.expensesChange > 0
        ? `Vos dépenses ont augmenté de ${stats.expensesChange.toFixed(1).replace('.', ',')} % vs le mois dernier. Surveillez vos postes clés.`
        : 'Vos dépenses sont stables par rapport au mois dernier. Continuez ainsi.';

  const handleFormSubmit = async (transaction: any) => {
    const result = await addTransaction(transaction);
    if (result.success) {
      setIsFormOpen(false);
    } else {
      alert(`Erreur lors de l'ajout : ${result.error}`);
    }
    return result;
  };

  const isInitialLoad =
    !hasLoadedOnce && (loading || transactionsLoading || accountsLoading || categoriesLoading);
  if (isInitialLoad) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-11 w-11 animate-spin rounded-full border-2 border-primary-500/20 border-t-primary-500" />
          <p className="mt-4 text-sm text-dark-400">Chargement du tableau de bord…</p>
        </div>
      </div>
    );
  }

  const signedAmount = (t: any) => {
    const abs = Math.abs(Number(t.montant) || 0);
    if (t.type === 'depense') return -abs;
    if (t.type === 'revenu') return abs;
    return Number(t.montant) || 0;
  };

  return (
    <div className="space-y-5">
      <div className="animate-fade-up flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-h1 text-white">Bonjour {greetingName} 👋</h1>
          <p className="mt-1.5 text-body text-dark-400">
            Voici un aperçu clair de vos finances ce mois-ci.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 self-start rounded-2xl border border-white/[0.06] bg-dark-960/80 px-3 py-2 text-body-sm font-medium text-dark-300 shadow-card">
          <CalendarDays className="h-4 w-4 text-primary-400" />
          {periodLabel}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          delay={0}
          label="Solde total"
          value={formatCurrency(stats.totalBalance)}
          numericValue={stats.totalBalance}
          valueSuffix=" €"
          change={stats.balanceChange}
          sparkValues={balanceSeries}
          sparkColor="#00E676"
        />
        <MetricCard
          delay={0.05}
          label="Revenus mensuels"
          value={formatCurrency(stats.monthlyIncome)}
          numericValue={stats.monthlyIncome}
          valueSuffix=" €"
          change={stats.incomeChange}
          sparkValues={incomeSeries}
          sparkColor="#5B8CFF"
        />
        <MetricCard
          delay={0.1}
          label="Dépenses mensuelles"
          value={formatCurrency(stats.monthlyExpenses)}
          numericValue={stats.monthlyExpenses}
          valueSuffix=" €"
          change={stats.expensesChange}
          invertChange
          sparkValues={expenseSeries}
          sparkColor="#F472B6"
        />
        <MetricCard
          delay={0.15}
          label="Taux d'épargne"
          value={`${stats.savingsRate.toFixed(1).replace('.', ',')} %`}
          numericValue={stats.savingsRate}
          valueSuffix=" %"
          valueDecimals={1}
          change={stats.savingsRateChange}
          ringValue={Math.max(0, Math.min(100, stats.savingsRate))}
          ringColor="#22D3EE"
        />
      </div>

      <ForecastWidget />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <div className="card-premium animate-fade-up stagger-5 xl:col-span-3">
          <div className="relative z-[1] mb-4 flex items-center justify-between gap-3">
            <h3 className="text-h3 text-white">Dépenses par catégorie</h3>
            <button
              type="button"
              onClick={() => navigate('/charts')}
              className="text-body-sm font-semibold text-primary-400 transition hover:text-primary-500"
            >
              Voir toutes les catégories
            </button>
          </div>
          {categoryStats.length === 0 ? (
            <div className="relative z-[1] py-10 text-center">
              <p className="text-body text-dark-400">Aucune dépense ce mois-ci</p>
              <p className="mt-1 text-body-sm text-dark-500">
                Ajoutez des transactions pour voir la répartition
              </p>
            </div>
          ) : (
            <div className="relative z-[1] flex flex-col items-center gap-6 md:flex-row md:items-center">
              <div className="shrink-0">
                <DonutChart
                  data={categoryStats}
                  size={210}
                  strokeWidth={26}
                  maxSlices={6}
                  centerPrimary={formatCurrency(stats.monthlyExpenses)}
                  centerSecondary="Total"
                />
              </div>
              <div className="w-full flex-1 space-y-3">
                {categoryStats.map((category) => (
                  <div key={category.name} className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full shadow-[0_0_10px_currentColor]"
                        style={{ backgroundColor: category.color, color: category.color }}
                      />
                      <span className="truncate text-body font-medium text-white">
                        {category.name}
                      </span>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-body font-semibold tabular-nums text-white">
                        {formatCurrency(category.amount)}
                      </p>
                      <p className="text-caption text-dark-500">{category.percentage} %</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card-premium animate-fade-up stagger-6 xl:col-span-2">
          <div className="relative z-[1] mb-3 flex items-center justify-between">
            <h3 className="text-h3 text-white">Transactions récentes</h3>
          </div>
          <div className="relative z-[1] space-y-1.5">
            {recentTransactions.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-body text-dark-400">Aucune transaction</p>
              </div>
            ) : (
              recentTransactions.map((transaction) => {
                const amount = signedAmount(transaction);
                const color = transaction.categorie?.couleur || '#64748b';
                const initial =
                  (transaction.description || transaction.categorie?.nom || '?')
                    .charAt(0)
                    .toUpperCase();
                return (
                  <button
                    key={transaction.id}
                    type="button"
                    onClick={() => navigate('/transactions')}
                    className="flex w-full items-center justify-between gap-3 rounded-xl px-2 py-2.5 text-left transition hover:bg-white/[0.03]"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-body-sm font-bold text-white"
                        style={{ backgroundColor: `${color}33`, color, boxShadow: `0 0 16px ${color}33` }}
                      >
                        {initial}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-body font-semibold text-white">
                          {transaction.description || 'Sans description'}
                        </p>
                        <p className="truncate text-body-sm text-dark-500">
                          {transaction.categorie?.nom || 'Sans catégorie'} ·{' '}
                          {new Date(transaction.date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                          })}
                        </p>
                      </div>
                    </div>
                    <p
                      className={`shrink-0 text-body font-bold tabular-nums ${
                        amount < 0 ? 'text-red-400' : 'text-primary-400'
                      }`}
                    >
                      {amount < 0 ? '−' : '+'}
                      {formatCurrency(Math.abs(amount))}
                    </p>
                  </button>
                );
              })
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsFormOpen(true)}
            className="relative z-[1] mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-2.5 text-body font-bold text-onPrimary shadow-glow-green transition hover:bg-primary-400"
          >
            <Plus className="h-4 w-4" />
            Nouvelle transaction
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="card-premium animate-fade-up lg:col-span-1">
          <div className="relative z-[1] flex gap-4">
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-center gap-1.5 text-primary-400">
                <Sparkles className="h-4 w-4" />
                <span className="text-body-sm font-semibold">Insight du moment</span>
              </div>
              <p className="text-body leading-relaxed text-dark-300">{insightText}</p>
            </div>
            <div className="shrink-0 self-center opacity-90">
              <Sparkline
                values={expenseSeries.slice(-7)}
                color={stats.expensesChange <= 0 ? '#00E676' : '#F472B6'}
                width={72}
                height={40}
              />
            </div>
          </div>
        </div>

        <div className="card-premium animate-fade-up">
          <div className="relative z-[1]">
            <p className="text-body-sm font-medium text-dark-400">
              {topBudgetCategory ? `Budget ${topBudgetCategory.name}` : 'Budget du mois'}
            </p>
            <p className="mt-1 text-body font-bold text-white">
              {topBudgetCategory ? `${budgetUsedPct} % utilisé` : 'Aucun budget'}
            </p>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/[0.06]">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  budgetUsedPct >= 80 ? 'bg-red-400 shadow-glow-red' : 'bg-primary-500 shadow-glow-green'
                }`}
                style={{ width: `${topBudgetCategory ? budgetUsedPct : 0}%` }}
              />
            </div>
          </div>
        </div>

        <div className="card-premium animate-fade-up">
          <div className="relative z-[1] flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-body-sm font-medium text-dark-400">Épargne ce mois</p>
              <p
                className={`kpi-value mt-1 ${
                  monthlySavings >= 0 ? 'text-white' : '!text-red-400'
                }`}
              >
                {formatCurrency(monthlySavings)}
              </p>
            </div>
            <RingProgress
              value={Math.max(0, Math.min(100, stats.savingsRate))}
              color="#22D3EE"
              size={54}
            />
          </div>
        </div>
      </div>

      <TransactionForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        title="Nouvelle transaction"
      />
    </div>
  );
};

export default Dashboard;
